import {
  Box,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";

export interface LabeledTextFieldProps
  extends Omit<TextFieldProps, "required" | "variant"> {
  label?: string;
  required?: boolean;
  maxLength?: number;
  containerSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  variant?: "filled" | "outlined" | "standard";
  height?: number | string;
  borderRadius?: number | string;
  paddingRight?: number | string;
}

export const TextFieldCustomLabel: React.FC<{
  label: string;
  required?: boolean;
  labelSx?: SxProps<Theme>;
}> = ({ label, required = false, labelSx }) => {
  return (
    <Typography
      component="label"
      sx={{
        fontSize: 12,
        fontWeight: 400,
        ...labelSx,
      }}
    >
      {label}
      {required && (
        <>
          {"  "}
          <Typography
            component="span"
            sx={{
              color: "red",
              fontSize: 12,
            }}
          >
            *
          </Typography>
        </>
      )}
    </Typography>
  );
};

export const LabeledTextField: React.FC<LabeledTextFieldProps> = ({
  label,
  required = false,
  maxLength,
  containerSx,
  labelSx,
  sx,
  inputProps,
  variant = "outlined",
  height = 48,
  borderRadius = "16px",
  paddingRight,
  ...textFieldProps
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ position: "relative", ...containerSx }}>
      {label && (
        <TextFieldCustomLabel
          label={label}
          labelSx={labelSx}
          required={required}
        />
      )}
      <TextField
        fullWidth
        inputProps={{
          spellCheck: false,
          title: "",
          ...(maxLength && { maxLength }),
          ...inputProps,
        }}
        required={required}
        sx={{
          mb: 2,
          ...sx,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#F2F2F7",
            borderRadius,
            height: height,
            paddingRight,
            "& fieldset": {
              border: "none",
            },
            "&:hover fieldset": {
              border: "none",
            },
            "&.Mui-focused fieldset": {
              border: "none",
            },
            "&.Mui-error fieldset": {
              border: `1px solid ${theme.colors.red}`,
            },
            "& input::placeholder": {
              color: theme.colors.text[0],
              fontSize: 14,
              fontStyle: "italic",
              opacity: 1,
            },
            "& textarea::placeholder": {
              color: theme.colors.text[0],
              fontSize: 14,
              fontStyle: "italic",
              opacity: 1,
            },
          },
          "& .MuiFilledInput-root": {
            bgcolor: theme.colors.secondary[1],
            borderRadius: "16px",
            "&:before": {
              borderBottom: "none",
            },
            "&:after": {
              borderBottom: "none",
            },
            "&:hover:not(.Mui-disabled):before": {
              borderBottom: "none",
            },
          },
          "& .MuiInput-underline": {
            "&:before": {
              borderBottom: "none",
            },
            "&:after": {
              borderBottom: "none",
            },
            "&:hover:not(.Mui-disabled):before": {
              borderBottom: "none",
            },
          },
          "& .MuiFormHelperText-root": {
            position: "absolute",
            bottom: "-20px",
            left: "12px",
            right: "12px",
            margin: 0,
            fontSize: "11px",
            padding: "2px 6px",
            borderRadius: "4px",
            zIndex: 1,
            color: theme.colors.red,
          },
        }}
        variant={variant}
        {...textFieldProps}
      />
    </Box>
  );
};
